/**
 * PromotionGatesPanel — the four promotion gates G0..G3.
 *
 * Renders the four promotion gates that a change must clear, in order,
 * to enter the governed cone. Each gate is paired with its required
 * artifacts and the refusal mode if the gate fails.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import React from "react";
import { PanelSurface, panelTokens } from "@csk/demo-ui";
import gatesData from "../data/gates.json" with { type: "json" };

const ACCENT = "#38bdf8";

interface Gate {
  readonly id: string;
  readonly label: string;
  readonly tagline: string;
  readonly required: readonly string[];
  readonly refusalMode: string;
}

const GATES: readonly Gate[] = gatesData.gates;

export function PromotionGatesPanel(): React.JSX.Element {
  return (
    <PanelSurface.PanelShell
      title="Promotion Gates"
      subtitle="G0..G3 — a change clears every applicable gate, in order, or it does not promote"
      accentColor={ACCENT}
    >
      <PanelSurface.Card>
        <p className="m-0 text-[14.5px] leading-[1.7] text-[var(--ilg-text)]">
          Promotion is monotone: a change moves through G0 → G1 → G2 → G3
          (bridge only if needed), and each gate is fail-closed. There is no
          &ldquo;skip a gate&rdquo; sequence — the gate set is a totally ordered
          chain, not a checklist with a quorum rule.
        </p>
      </PanelSurface.Card>

      <div className="relative pl-8">
        <div
          aria-hidden
          className="absolute top-[18px] bottom-[18px] left-[18px] w-0.5 rounded-[1px]"
          style={{
            background: `linear-gradient(to bottom, ${ACCENT}, ${ACCENT}22)`,
          }}
        />
        <div className="flex flex-col gap-3.5">
          {GATES.map((gate, index) => (
            <div key={gate.id} className="relative">
              <span
                aria-hidden
                className="absolute left-[-22px] top-[18px] w-[24px] h-[24px] rounded-full bg-[var(--ilg-elev)] flex items-center justify-center font-mono font-extrabold text-[11px]"
                style={{
                  border: `2px solid ${ACCENT}`,
                  boxShadow: `0 0 14px ${ACCENT}55`,
                  color: ACCENT,
                }}
              >
                {index}
              </span>
              <PanelSurface.Card>
                <div className="flex items-baseline gap-3 flex-wrap">
                  <span
                    className="font-mono text-[20px] font-extrabold"
                    style={{
                      color: ACCENT,
                      textShadow: `0 0 14px ${ACCENT}55`,
                    }}
                  >
                    {gate.id}
                  </span>
                  <span className="text-[15.5px] font-bold text-[var(--ilg-bright)]">
                    {gate.label}
                  </span>
                </div>
                <p className="m-0 mt-1.5 text-[12.5px] text-[var(--ilg-muted)] font-mono tracking-[0.04em]">
                  {gate.tagline}
                </p>
                <PanelSurface.SectionHeading style={{ marginTop: 14 }}>
                  Required artifacts
                </PanelSurface.SectionHeading>
                <div className="mt-2 flex gap-1.5 flex-wrap">
                  {gate.required.map((r) => PanelSurface.badge(r, panelTokens.accentBlue))}
                </div>
                <PanelSurface.SectionHeading style={{ marginTop: 14 }}>
                  Refusal mode
                </PanelSurface.SectionHeading>
                <p className="mt-2 text-[13.5px] text-[var(--ilg-text)] leading-[1.7]">
                  {gate.refusalMode}
                </p>
              </PanelSurface.Card>
            </div>
          ))}
        </div>
      </div>
    </PanelSurface.PanelShell>
  );
}
